import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, CircularProgress,
  InputAdornment, TextField, Tooltip, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, CloudUploadOutlined, CheckCircleOutlined,
  ImageOutlined, VideoLibraryOutlined, BrokenImageOutlined,
  SearchOutlined, ClearOutlined, DeleteOutlineOutlined,
  CropOutlined, HistoryOutlined,
} from '@mui/icons-material';
import { api } from '../../../hooks/useApi';
import { CropEditorModal } from './CropEditorModal';

const PRIMARY = '#1075BA';
const DANGER  = '#dc2626';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── UploadCard ────────────────────────────────────────────────────────────────

function UploadCard({ uploading, onFileSelected }) {
  return (
    <Box
      onClick={onFileSelected}
      sx={{
        aspectRatio: '16/9',
        borderRadius: '10px',
        border: `2px dashed ${uploading ? PRIMARY : '#c8d4e0'}`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 0.5,
        cursor: uploading ? 'default' : 'pointer',
        background: uploading ? alpha(PRIMARY, 0.04) : '#f8fafc',
        transition: 'all 0.15s',
        '&:hover': !uploading ? {
          borderColor: PRIMARY,
          background: alpha(PRIMARY, 0.04),
        } : {},
      }}
    >
      {uploading ? (
        <>
          <CircularProgress size={18} sx={{ color: PRIMARY }} />
          <Typography sx={{ fontSize: '0.65rem', color: PRIMARY, fontWeight: 600 }}>
            Uploading…
          </Typography>
        </>
      ) : (
        <>
          <CloudUploadOutlined sx={{ fontSize: 22, color: '#b0bac8' }} />
          <Typography sx={{
            fontSize: '0.65rem', fontWeight: 600, color: '#9ea8b3',
            textAlign: 'center', px: 1, lineHeight: 1.4,
          }}>
            Click or drop<br />to upload
          </Typography>
        </>
      )}
    </Box>
  );
}

// ── MediaCard ─────────────────────────────────────────────────────────────────

function MediaCard({ item, selected, onSelect, mediaType }) {
  const [imgError, setImgError] = useState(false);
  const isSelected = selected?._id === item._id;

  // Show the cropped thumbnail if available, else the original
  const thumbSrc = item.croppedThumbnailUrl || item.thumbnailUrl || item.url;
  const displayName = item.displayName || item.originalName || item.publicId?.split('/').pop();

  return (
    <Box
      onClick={() => onSelect(item)}
      sx={{
        position: 'relative',
        borderRadius: '10px',
        overflow: 'hidden',
        border: `2px solid ${isSelected ? PRIMARY : '#e8edf2'}`,
        cursor: 'pointer',
        transition: 'all 0.15s',
        background: '#f0f4f8',
        aspectRatio: '16/9',
        '&:hover': {
          border: `2px solid ${isSelected ? PRIMARY : alpha(PRIMARY, 0.4)}`,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      }}
    >
      {!imgError ? (
        <Box
          component="img"
          src={thumbSrc}
          alt={displayName}
          onError={() => setImgError(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Box sx={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 0.5, background: '#e8edf2',
        }}>
          <BrokenImageOutlined sx={{ fontSize: 24, color: '#b0bac8' }} />
          <Typography sx={{ fontSize: '0.6rem', color: '#b0bac8' }}>No preview</Typography>
        </Box>
      )}

      {mediaType === 'video' && item.duration && (
        <Box sx={{
          position: 'absolute', top: 5, left: 5,
          background: 'rgba(0,0,0,0.6)', borderRadius: '4px',
          px: 0.6, py: 0.2,
          display: 'flex', alignItems: 'center', gap: 0.4,
        }}>
          <VideoLibraryOutlined sx={{ fontSize: 10, color: '#fff' }} />
          <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>
            {formatDuration(item.duration)}
          </Typography>
        </Box>
      )}

      {/* Cropped badge */}
      {item.isCropped && (
        <Box sx={{
          position: 'absolute', top: 5, right: 5,
          background: 'rgba(16,117,186,0.85)', borderRadius: '4px',
          px: 0.6, py: 0.15,
          display: 'flex', alignItems: 'center', gap: 0.3,
        }}>
          <CropOutlined sx={{ fontSize: 9, color: '#fff' }} />
          <Typography sx={{ fontSize: '0.55rem', color: '#fff', fontWeight: 700 }}>
            Cropped
          </Typography>
        </Box>
      )}

      {item.bytes > 0 && (
        <Box sx={{
          position: 'absolute', bottom: 22, right: 5,
          background: 'rgba(0,0,0,0.55)', borderRadius: '4px',
          px: 0.6, py: 0.1,
        }}>
          <Typography sx={{ fontSize: '0.58rem', color: '#fff' }}>
            {formatBytes(item.bytes)}
          </Typography>
        </Box>
      )}

      {isSelected && (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: alpha(PRIMARY, 0.18),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '50%', background: PRIMARY,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}>
            <CheckCircleOutlined sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
        </Box>
      )}

      {/* Filename overlay */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
        px: 0.75, pt: 1.5, pb: 0.4,
      }}>
        <Typography sx={{
          fontSize: '0.6rem', color: '#fff', lineHeight: 1.2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {displayName}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function PagBtn({ children, onClick, disabled, active }) {
  return (
    <Button
      size="small"
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      disabled={disabled}
      sx={{
        minWidth: 32, px: 0, fontSize: '0.72rem', borderRadius: '7px',
        borderColor: '#d4dbe6',
        background:  active ? PRIMARY : 'transparent',
        color:       active ? '#fff'  : '#5a6474',
        '&:hover':   { background: active ? alpha(PRIMARY, 0.85) : '#f0f4f8' },
      }}
    >
      {children}
    </Button>
  );
}

function PaginationBar({ pagination, onPage }) {
  const { page, totalPages, total, limit } = pagination;
  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);
  if (totalPages <= 1) return null;

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      mt: 2, pt: 1.5, borderTop: '1px solid #e8edf2',
    }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3' }}>
        {from}–{to} of {total}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <PagBtn disabled={!pagination.hasPrev} onClick={() => onPage(page - 1)}>‹</PagBtn>
        {pages.map((p, i) =>
          p === '…'
            ? <Typography key={`e${i}`} sx={{ fontSize: '0.72rem', color: '#b0bac8', px: 0.5, lineHeight: '30px' }}>…</Typography>
            : <PagBtn key={p} active={p === page} onClick={() => onPage(p)}>{p}</PagBtn>
        )}
        <PagBtn disabled={!pagination.hasNext} onClick={() => onPage(page + 1)}>›</PagBtn>
      </Box>
    </Box>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

/**
 * ContentBrowserModal
 *
 * Props:
 *   open      — boolean
 *   onClose   — () => void
 *   onSelect  — (url: string) => void
 *   mediaType — 'image' | 'video'
 */
export function ContentBrowserModal({ open, onClose, onSelect, mediaType = 'image', currentUrl = '', forcedAspectRatio = null }) {
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [pagination, setPagination]   = useState(null);
  const [page, setPage]               = useState(1);
  const [searchRaw, setSearchRaw]     = useState('');
  const [selected, setSelected]       = useState(null);
  const [error, setError]             = useState('');
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [deleting, setDeleting]       = useState(false);
  const [reverting, setReverting]     = useState(false);
  const [draggingOver, setDraggingOver] = useState(false);

  // Crop editor state
  const [cropOpen, setCropOpen]       = useState(false);

  const fileInputRef = useRef();
  const dragCountRef = useRef(0);

  const search = useDebounce(searchRaw, 400);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchMedia = useCallback(async (pg = 1, q = '') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ type: mediaType, page: pg, limit: 20, sort: 'newest' });
      if (q.trim()) params.set('search', q.trim());
      const { data } = await api.get(`/media?${params}`);
      const items = data.data.items;
      setItems(items);
      setPagination(data.data.pagination);
      return items;
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load media');
      return [];
    } finally {
      setLoading(false);
    }
  }, [mediaType]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setSearchRaw('');
    setPage(1);
    setUploadError('');

    fetchMedia(1, '').then(async (loadedItems) => {
      if (!currentUrl) return;
      const match = loadedItems.find(i => i.url === currentUrl || i.croppedUrl === currentUrl);
      if (match) { setSelected(match); return; }

      try {
        const filename = currentUrl.split('/').pop().split('?')[0];
        const { data } = await api.get(
          `/media?type=${mediaType}&search=${encodeURIComponent(filename)}&limit=5`
        );
        const found = data.data?.items?.find(i => i.url === currentUrl || i.croppedUrl === currentUrl);
        if (found) setSelected(found);
      } catch (_) {}
    });
  }, [open, mediaType]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    fetchMedia(page, search);
  }, [page, search]); // eslint-disable-line

  // ── Upload ──────────────────────────────────────────────────────────────────

  const doUpload = useCallback(async (file) => {
    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/media/upload', formData);
      const newItem = data.data;
      setItems(prev => [newItem, ...prev]);
      setSelected(newItem);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/media/${selected._id}`);
      setItems(prev => prev.filter(i => i._id !== selected._id));
      setSelected(null);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [selected]);

  // ── Revert crop ─────────────────────────────────────────────────────────────

  const handleRevert = useCallback(async () => {
    if (!selected || !selected.isCropped) return;
    setReverting(true);
    setUploadError('');
    try {
      const { data } = await api.post(`/media/${selected._id}/revert`);
      const updated = data.data;
      setItems(prev => prev.map(i => i._id === updated._id ? updated : i));
      setSelected(updated);
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || 'Revert failed');
    } finally {
      setReverting(false);
    }
  }, [selected]);

  // ── Crop saved callback ─────────────────────────────────────────────────────

  const handleCropSaved = useCallback((updatedItem) => {
    setItems(prev => prev.map(i => i._id === updatedItem._id ? updatedItem : i));
    setSelected(updatedItem);
  }, []);

  // ── Drag & drop ─────────────────────────────────────────────────────────────

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCountRef.current += 1;
    if (dragCountRef.current === 1) setDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCountRef.current -= 1;
    if (dragCountRef.current === 0) setDraggingOver(false);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    dragCountRef.current = 0;
    setDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (mediaType === 'image' && !isImage) { setUploadError('Only image files allowed'); return; }
    if (mediaType === 'video' && !isVideo) { setUploadError('Only video files allowed'); return; }
    doUpload(file);
  }, [doUpload, mediaType]);

  // ── Confirm selection ───────────────────────────────────────────────────────

  const handleConfirm = () => {
    if (!selected) return;
    // Prefer the cropped URL if it exists, otherwise original
    onSelect(selected.croppedUrl || selected.url);
    onClose();
    setSelected(null);
  };

  const handleSearchChange = (val) => { setSearchRaw(val); setPage(1); };
  const handlePage = (p) => { setPage(p); setSelected(null); };

  const TypeIcon = mediaType === 'video' ? VideoLibraryOutlined : ImageOutlined;
  const accept   = mediaType === 'video'
    ? 'video/mp4,video/webm,video/ogg,video/quicktime'
    : 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml';

  const canCrop = selected && mediaType === 'image';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            height: '88vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) doUpload(e.target.files[0]); e.target.value = ''; }}
        />

        {/* ── Header ── */}
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.75, px: 2.5, borderBottom: '1px solid #e8edf2', flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '8px',
              background: alpha(PRIMARY, 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: PRIMARY,
            }}>
              <TypeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', lineHeight: 1 }}>
                {mediaType === 'video' ? 'Video' : 'Image'} Library
              </Typography>
              <Typography sx={{ fontSize: '0.63rem', color: '#9ea8b3', mt: 0.2 }}>
                {pagination ? `${pagination.total} file${pagination.total !== 1 ? 's' : ''}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        {/* ── Search bar ── */}
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #f0f4f8', flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            value={searchRaw}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder={`Search ${mediaType === 'video' ? 'videos' : 'images'} by name or tag…`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 17, color: '#9ea8b3' }} />
                </InputAdornment>
              ),
              endAdornment: searchRaw ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => handleSearchChange('')} sx={{ color: '#b0bac8' }}>
                    <ClearOutlined sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: '9px', fontSize: '0.8rem',
                '& fieldset': { borderColor: '#e4e9f0' },
                '&:hover fieldset': { borderColor: alpha(PRIMARY, 0.4) },
                '&.Mui-focused fieldset': { borderColor: PRIMARY },
              },
            }}
          />
        </Box>

        {/* ── Body — drag-and-drop target ── */}
        <DialogContent
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            flex: 1, overflowY: 'auto', p: 2.5,
            position: 'relative',
            outline: draggingOver ? `2px dashed ${PRIMARY}` : '2px dashed transparent',
            outlineOffset: '-6px',
            background: draggingOver ? alpha(PRIMARY, 0.025) : 'transparent',
            transition: 'outline-color 0.15s, background 0.15s',
          }}
        >
          {draggingOver && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1,
            }}>
              <CloudUploadOutlined sx={{ fontSize: 44, color: PRIMARY, opacity: 0.55 }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: PRIMARY, opacity: 0.75 }}>
                Drop to upload
              </Typography>
            </Box>
          )}

          {uploadError && (
            <Typography sx={{ fontSize: '0.7rem', color: DANGER, mb: 1.5, fontWeight: 500 }}>
              {uploadError}
            </Typography>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 6 }}>
              <CircularProgress size={32} sx={{ color: PRIMARY }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', pt: 5 }}>
              <Typography sx={{ fontSize: '0.8rem', color: DANGER }}>{error}</Typography>
              <Button size="small" onClick={() => fetchMedia(page, search)} sx={{ mt: 1 }}>Retry</Button>
            </Box>
          ) : (
            <>
              {items.length === 0 ? (
                <Box sx={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: '100%', flexDirection: 'column', gap: 0.75,
                }}>
                  {uploading ? (
                    <>
                      <CircularProgress size={18} sx={{ color: PRIMARY }} />
                      <Typography sx={{ fontSize: '0.65rem', color: PRIMARY, fontWeight: 600 }}>
                        Uploading…
                      </Typography>
                    </>
                  ) : (
                    <>
                      <TypeIcon sx={{ fontSize: 32, color: '#d4dbe6' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#b0bac8' }}>
                        {searchRaw
                          ? `No results for "${searchRaw}"`
                          : 'No files yet. Drop one or click the upload button'}
                      </Typography>
                      {!searchRaw && (
                        <Button
                          variant="contained"
                          onClick={() => !uploading && fileInputRef.current?.click()}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          Upload
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 1.5,
                  }}>
                    <UploadCard
                      uploading={uploading}
                      onFileSelected={() => !uploading && fileInputRef.current?.click()}
                    />
                    {items.map(item => (
                      <MediaCard
                        key={item._id}
                        item={item}
                        selected={selected}
                        onSelect={setSelected}
                        mediaType={mediaType}
                      />
                    ))}
                  </Box>

                  {pagination && items.length > 0 && (
                    <PaginationBar pagination={pagination} onPage={handlePage} />
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions sx={{
          px: 2.5, py: 1.75, borderTop: '1px solid #e8edf2', flexShrink: 0,
          justifyContent: 'space-between', alignItems: 'center',
        }}>
          {/* Left: selected preview + actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            {selected ? (
              <>
                {/* Thumbnail */}
                <Box
                  component="img"
                  src={selected.croppedThumbnailUrl || selected.thumbnailUrl || selected.url}
                  sx={{
                    width: 36, height: 36, borderRadius: '6px',
                    objectFit: 'cover', border: '1px solid #e8edf2', flexShrink: 0,
                  }}
                />

                {/* Name + dimensions */}
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{
                      fontSize: '0.72rem', fontWeight: 600, color: '#2c3444',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
                    }}>
                      {selected.displayName || selected.originalName}
                    </Typography>
                    {selected.isCropped && (
                      <Chip
                        label="Cropped"
                        size="small"
                        icon={<CropOutlined sx={{ fontSize: '9px !important' }} />}
                        sx={{
                          height: 16, fontSize: '0.58rem', fontWeight: 700,
                          background: alpha(PRIMARY, 0.1), color: PRIMARY,
                          border: `1px solid ${alpha(PRIMARY, 0.25)}`,
                          '& .MuiChip-icon': { color: PRIMARY },
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.62rem', color: '#9ea8b3' }}>
                    {selected.width && selected.height ? `${selected.width}×${selected.height} · ` : ''}
                    {formatBytes(selected.bytes)}
                  </Typography>
                </Box>

                {/* Delete */}
                <Tooltip title="Delete file">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDelete}
                      disabled={deleting}
                      sx={{
                        flexShrink: 0, color: DANGER,
                        border: `1px solid ${alpha(DANGER, 0.3)}`,
                        borderRadius: '7px', p: '4px',
                        '&:hover': { background: alpha(DANGER, 0.07), borderColor: DANGER },
                        '&:disabled': { color: '#b0bac8', borderColor: '#e8edf2' },
                      }}
                    >
                      {deleting
                        ? <CircularProgress size={14} sx={{ color: DANGER }} />
                        : <DeleteOutlineOutlined sx={{ fontSize: 15 }} />
                      }
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Revert — only visible when the image has been cropped */}
                {selected.isCropped && (
                  <Tooltip title="Revert to original (undo crop)">
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleRevert}
                        disabled={reverting}
                        sx={{
                          flexShrink: 0, color: '#7c8fa8',
                          border: '1px solid #d4dbe6',
                          borderRadius: '7px', p: '4px',
                          '&:hover': { background: '#f0f4f8', borderColor: '#9ea8b3', color: '#3a4a5c' },
                          '&:disabled': { color: '#b0bac8', borderColor: '#e8edf2' },
                        }}
                      >
                        {reverting
                          ? <CircularProgress size={14} sx={{ color: '#9ea8b3' }} />
                          : <HistoryOutlined sx={{ fontSize: 15 }} />
                        }
                      </IconButton>
                    </span>
                  </Tooltip>
                )}


              </>
            ) : (
              <Typography sx={{ fontSize: '0.72rem', color: '#b0bac8', fontStyle: 'italic' }}>
                No file selected
              </Typography>
            )}
          </Box>

          {/* Right: Cancel + Edit (Crop) + Select */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={onClose}
              size="small"
              sx={{
                borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem',
                borderColor: '#d4dbe6', color: '#5a6474',
                '&:hover': { borderColor: '#9ea8b3' },
              }}
            >
              Cancel
            </Button>
            {canCrop && (
              <Button
                variant="outlined"
                onClick={() => setCropOpen(true)}
                size="small"
                startIcon={<CropOutlined sx={{ fontSize: 14 }} />}
                sx={{
                  borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem',
                  fontWeight: 600,
                  borderColor: alpha(PRIMARY, 0.35), color: PRIMARY,
                  '&:hover': { background: alpha(PRIMARY, 0.05), borderColor: PRIMARY },
                }}
              >
                Edit
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={!selected}
              size="small"
              sx={{
                borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem',
                fontWeight: 700, background: PRIMARY, px: 2.5,
                '&:hover': { background: alpha(PRIMARY, 0.85) },
                '&:disabled': { background: '#d4dbe6', color: '#9ea8b3' },
              }}
            >
              Select
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Crop editor — mounted outside the main dialog ── */}
      {cropOpen && (
        <CropEditorModal
          open={cropOpen}
          onClose={() => setCropOpen(false)}
          item={selected}
          onSaved={handleCropSaved}
          forcedAspectRatio={forcedAspectRatio}
        />
      )}
    </>
  );
}